import React, { useState } from "react";
import { cn } from "../../lib/utils";

export function NavigationTabsV3({
  tabs = [
    { id: "loans", label: "Loans", path: "/borrower-portal/loans", active: true },
    {
      id: "draw-request",
      label: "Draw Request",
      path: "/draw-request/loans",
      active: false,
    },
  ],
  onTabClick,
  className = "",
  activeRoute = "/borrower-portal/loans",
  navItemClassName = "",
  navItemActiveClassName = "",
  navItemActiveBarClassName = "",
  mobileTriggerClassName = "",
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const getIsActive = (tab) => {
    const routeParts = activeRoute?.split("/") || [];
    const targetPart = routeParts.length > 1 ? routeParts[1] : "";
    return targetPart && tab?.path ? tab.path.includes(targetPart) : false;
  };

  const activeTab = tabs.find(getIsActive) || tabs[0];

  const handleMobileTabClick = (tab) => {
    setMobileOpen(false);
    onTabClick(tab);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav
        className={cn("hidden lg:flex items-center space-x-8 relative", className)}
        role="navigation"
        aria-label="Main navigation"
      >
        {tabs.map((tab) => (
          <NavItem
            key={tab?.id}
            label={tab?.label}
            isActive={getIsActive(tab)}
            onClick={() => onTabClick(tab)}
            navItemClassName={navItemClassName}
            navItemActiveClassName={navItemActiveClassName}
            navItemActiveBarClassName={navItemActiveBarClassName}
          />
        ))}
      </nav>

      {/* Mobile Dropdown Trigger */}
      <div className="lg:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className={cn(
            "flex items-center gap-1.5 font-semibold text-gray-900 focus:outline-none text-md leading-normal",
            mobileTriggerClassName
          )}
          aria-haspopup="true"
          aria-expanded={mobileOpen}
        >
          {activeTab?.label}
          <svg
            className="w-4 h-4 text-gray-700 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Bottom Sheet */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />

          {/* Sheet */}
          <div className="relative w-full bg-white rounded-t-3xl z-10">
            {/* Close button — floats above the sheet on the overlay */}
            <div className="absolute -top-14 left-1/2 -translate-x-1/2 z-20">
              <button
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center focus:outline-none"
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5 text-gray-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Title */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Switch View</h3>
            </div>

            {/* Tab Options */}
            <ul className="px-6 pb-8">
              {tabs.map((tab) => {
                const isActive = getIsActive(tab);
                return (
                  <li key={tab?.id} className="border-b border-gray-100 last:border-0">
                    <button
                      onClick={() => handleMobileTabClick(tab)}
                      className={cn(
                        "w-full text-left py-4 text-sm focus:outline-none",
                        isActive ? "text-primary-3 font-medium" : "text-gray-900 font-normal"
                      )}
                    >
                      {tab?.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

const NavItem = ({
  label,
  isActive = false,
  onClick,
  navItemClassName = "",
  navItemActiveClassName = "",
  navItemActiveBarClassName = "",
}) => {
  return (
    <button
      data-nav-item={label}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "text-sm font-medium transition-all duration-200 ease-in-out px-2 py-2 rounded-md relative first:pl-0",
        "hover:text-primary-3 hover:bg-primary-8 focus:outline-none",
        isActive ? "text-primary-3" : "text-gray-900",
        navItemClassName,
        isActive && navItemActiveClassName
      )}
    >
      {label}
      {isActive && (
        <div
          className={cn(
            "absolute -bottom-[1rem] left-0 right-0 h-[0.25rem] bg-primary-3",
            navItemActiveBarClassName
          )}
        />
      )}
    </button>
  );
};
